import React, { FC, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { SecondaryButton, PrimaryButton, Loader } from 'common/components';

import { ConfirmModalProps } from './models';
import { FormStyled, ButtonsSection, ModalStyled } from './styles';
import { Modal } from '@mui/material';
import { CloseIcon } from 'applicaiton/assets';

/**
 * ConfirmModal component
 *
 * This component renders a modal window with a form that can be used to confirm an action.
 * It accepts several props to customize its behavior and appearance.
 *
 * @param opened - A boolean indicating whether the modal is open or closed.
 * @param onClose - A function to call when the user closes the modal.
 * @param onSubmit - A function to call when the form is submitted.
 * @param children - The content of the form, typically a set of form fields and labels.
 * @param styleType - An optional string that specifies the type of styling for the primary button.
 *   Defaults to 'default' if not provided.
 * @param isLoading - A boolean indicating whether the modal is currently in a loading state.
 *
 * @returns The rendered ConfirmModal component.
 */
const ConfirmModal: FC<ConfirmModalProps> = ({
  opened,
  onClose,
  onSubmit,
  children,
  styleType = 'default',
  isLoading,
}) => {
  const { t } = useTranslation();
  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSubmit(event);
  };

  return (
    <Modal open={opened} onClose={onClose}>
      <>
        {isLoading && <Loader />}
        <ModalStyled>
          <CloseIcon onClick={onClose} className="modal-close-icon" />
          <div>
            <FormStyled onSubmit={handleSubmit}>
              {children}
              <ButtonsSection>
                <SecondaryButton type="button" onClick={onClose}>
                  {t('cancel')}
                </SecondaryButton>
                <PrimaryButton type="submit" styleType={styleType}>
                  {t('confirm')}
                </PrimaryButton>
              </ButtonsSection>
            </FormStyled>
          </div>
        </ModalStyled>
      </>
    </Modal>
  );
};

export default ConfirmModal;
